/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

'use client';

import {css, cx} from '@panda/css';
import {OptimizedAssetFragment} from './Asset.atom';
import {AnalyticEventProps} from '@/types';
import {useEmitEventOnClick} from '@/lib/utils/analytics';
import {SyntheticEvent, useEffect, useRef, useState} from 'react';
import {getOnKeyDownProp} from '@/lib/utils/a11y';
import {LOCAL_POSA_ASSETS_PATH} from '@/constants';

const buttonStyles = css({
	border: 0,
	fill: 'white',
	position: 'absolute',
	left: 0,
	cursor: 'pointer',
	right: 0,
	margin: '0 auto',
	top: '50%',
	transform: 'translateY(-50%)',
	background: 'black',
	width: '56px',
	borderRadius: '50px',
	height: '56px',
	paddingLeft: 'eds.dimension.12',
	display: 'inline-flex',
	justifyContent: 'center',
	alignItems: 'center'
});

export interface VideoProps {
	video: OptimizedAssetFragment;
	className?: string;
	disableAutoplay?: boolean;
	analytics?: AnalyticEventProps;
}

export const Video = ({className, video, disableAutoplay, analytics}: VideoProps) => {
	const emitEvent = useEmitEventOnClick(analytics!);
	const videoRef = useRef<HTMLVideoElement>(null);
	const {url, mimeType, fallbackImage} = video;
	const videoSrcAttrs = mimeType ? {type: mimeType} : {};
	const [showControls, setShowControls] = useState(false);

	// Show controls and play video.
	const handlePlayPause = (e: SyntheticEvent) => {
		e.stopPropagation();
		setShowControls((prev) => !prev);
	};

	useEffect(() => {
		if (!videoRef.current) return;
		if (showControls) {
			void videoRef.current.play();
			videoRef.current?.focus();
		} else {
			void videoRef.current.pause();
		}
	}, [showControls, videoRef.current]);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		const isInViewport = (el: Element, pad = 24) => {
			const r = el.getBoundingClientRect();
			const vh = window.innerHeight || document.documentElement.clientHeight;
			return r.top >= -pad && r.bottom <= vh + pad;
		};

		const scrollToVideo = (video: HTMLVideoElement) => {
			if (isInViewport(video)) return;
			// smooth scroll; center is nice for tall UIs
			video.scrollIntoView({behavior: 'smooth', block: 'center'});
		};

		const handlePause = (e: Event) => emitEvent(e, {action: 'Paused'});
		const handleEnded = (e: Event) => emitEvent(e, {action: 'Ended'});
		const handlePlay = (e: Event) => {
			emitEvent(e, {action: 'Played'});
			const v = e.currentTarget as HTMLVideoElement;
			// only react to user-initiated plays (avoid programmatic .play())
			if (e.isTrusted) scrollToVideo(v);
		};

		video.addEventListener('pause', handlePause);
		video.addEventListener('play', handlePlay);
		video.addEventListener('ended', handleEnded);

		return () => {
			video.removeEventListener('pause', handlePause);
			video.removeEventListener('play', handlePlay);
			video.removeEventListener('ended', handleEnded);
		};
	}, []);

	let overlayOnFirstPlay: typeof handlePlayPause | undefined = handlePlayPause;
	let videoPointerEvents: string | undefined = 'none';
	// clear, if show controls
	if (showControls) {
		overlayOnFirstPlay = undefined;
		videoPointerEvents = undefined;
	}

	return (
		<div
			className={css({position: 'relative', cursor: 'pointer'})}
			// Conditionally render these props because Firefox executes onKeyDown (stops propagation) even if focus is on child component (video).
			{...(!showControls && {
				onClick: overlayOnFirstPlay,
				// Important for accessibility - FDT-4996
				onKeyDown: getOnKeyDownProp((e) => overlayOnFirstPlay && overlayOnFirstPlay(e)),
				tabIndex: 0, // Focusable only once, before clicked.
				'aria-label': 'play how to redeem code video',
				role: 'button'
			})}>
			<video
				ref={videoRef}
				width={fallbackImage?.width || '100%'}
				poster={fallbackImage?.url || `${LOCAL_POSA_ASSETS_PATH}/video-placeholder.png`}
				key={`video-${url}`}
				title={video?.altText || ''}
				controls={showControls}
				playsInline
				tabIndex={showControls ? 0 : -1}
				disableRemotePlayback
				autoPlay={!disableAutoplay}
				controlsList="nodownload"
				className={cx(
					'video',
					className,
					css({objectFit: 'cover', pointerEvents: videoPointerEvents})
				)}>
				<source src={url} {...videoSrcAttrs} />
				{/* <source src={fallbackUrl} {...videoSrcAttrs} /> */}
				{/* cannot load from CDN, reference en-US locally */}
			</video>
			{!showControls && !fallbackImage?.url && (
				<button aria-label="play how to redeem your code" className={buttonStyles}>
					<svg
						role="presentation"
						width="39"
						height="40"
						viewBox="0 0 39 40"
						xmlns="http://www.w3.org/2000/svg"
						className={css({width: 'eds.dimension.20', height: 'fit-content'})}>
						<path d="M6.21102 39.4966L36.2778 23.5789C38.8805 22.201 38.8805 18.4725 36.2778 17.0946L6.21102 1.17695C3.76784 -0.116498 0.826172 1.65463 0.826172 4.41907V36.2545C0.826172 39.0189 3.76784 40.79 6.21102 39.4966Z" />
					</svg>
				</button>
			)}
		</div>
	);
};
